import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Store, ArrowRight, Loader2, AlertCircle, CheckCircle,
  Package, Users, BarChart3, Zap, Mail, Building, SkipForward, Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function DirectOnboardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('choice'); // 'choice', 'shopify', 'skip'
  const [shopDomain, setShopDomain] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  
  // Skip flow state
  const [skipForm, setSkipForm] = useState({
    companyName: '',
    email: '',
    fullName: '',
    password: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleConnectShopify = () => {
    if (!shopDomain) {
      setError('Please enter your Shopify store URL');
      return;
    }

    setConnecting(true);
    setError('');
    
    // Clean shop domain
    let cleanDomain = shopDomain.trim().toLowerCase();
    // Remove https:// or http:// if present
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '');
    // Remove trailing slashes
    cleanDomain = cleanDomain.replace(/\/+$/, '');
    // Remove /admin or any path
    cleanDomain = cleanDomain.split('/')[0];
    
    if (!cleanDomain.includes('.myshopify.com')) {
      cleanDomain = `${cleanDomain}.myshopify.com`;
    }

    // Redirect to existing Shopify install endpoint
    window.location.href = `${API_BASE_URL}/api/shopify-billing/install?shop=${cleanDomain}`;
  };

  const handleSkipShopify = () => {
    setStep('skip');
    setError('');
  };

  const handleSkipFormChange = (field, value) => {
    setSkipForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSkipSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!skipForm.companyName.trim()) {
      setError('Company name is required');
      return;
    }
    if (!skipForm.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!skipForm.fullName.trim()) {
      setError('Full name is required');
      return;
    }
    if (!skipForm.password) {
      setError('Password is required');
      return;
    }
    if (skipForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (skipForm.password !== skipForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/shopify-billing/direct-onboard`, {
        company_name: skipForm.companyName.trim(),
        email: skipForm.email.trim().toLowerCase(),
        full_name: skipForm.fullName.trim(),
        password: skipForm.password
      });

      if (response.data.token) {
        // Store token and redirect to dashboard
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Force full page reload to ensure AuthContext picks up the new token
        window.location.href = '/dashboard?welcome=true';
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create account. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: Package,
      title: 'Order Management',
      description: 'Track and manage all your orders'
    },
    {
      icon: BarChart3,
      title: 'Inventory Control',
      description: 'Real-time stock tracking'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Role-based access for your team'
    },
    {
      icon: Zap,
      title: 'Automation',
      description: 'Automated workflows'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 mb-6 shadow-lg shadow-purple-500/30">
              <Store className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Welcome to Opswarex
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {step === 'choice' && 'Get started in seconds. Set up your company and start managing orders, inventory, and team.'}
              {step === 'shopify' && 'Enter your Shopify store URL to connect and sync automatically.'}
              {step === 'skip' && 'Create your account to get started. You can connect Shopify later.'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {/* Choice Step */}
          {step === 'choice' && (
            <>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white/20">
                <h2 className="text-xl font-semibold text-white mb-6 text-center">
                  Let's get you started
                </h2>
                
                <div className="max-w-md mx-auto">
                  {/* Manual Setup Option - Now Primary */}
                  <div 
                    className="bg-white/5 rounded-xl p-6 border-2 border-purple-500/50 hover:border-purple-500 transition-all cursor-pointer group"
                    onClick={handleSkipShopify}
                  >
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4">
                      <Rocket className="w-7 h-7 text-purple-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Create Your Account
                    </h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Set up your company and start managing orders, inventory, and team. You can connect Shopify later from settings.
                    </p>
                    <div className="flex items-center text-purple-400 group-hover:text-purple-300">
                      <span className="text-sm font-medium">Get Started</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {features.map((feature, idx) => (
                  <div
                    key={idx}
                    className="bg-white/5 backdrop-blur rounded-xl p-4 border border-white/10"
                  >
                    <feature.icon className="w-6 h-6 text-purple-400 mb-2" />
                    <h3 className="text-sm font-semibold text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Shopify Connect Step */}
          {step === 'shopify' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white/20">
              <button 
                onClick={() => setStep('choice')}
                className="text-gray-400 hover:text-white mb-6 flex items-center gap-2 text-sm"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back
              </button>
              
              <div className="max-w-md mx-auto">
                <Label className="text-gray-300 mb-2 block">
                  Your Shopify Store URL
                </Label>
                <div className="flex mb-4">
                  <Input
                    type="text"
                    value={shopDomain}
                    onChange={(e) => {
                      setShopDomain(e.target.value);
                      setError('');
                    }}
                    placeholder="your-store"
                    className="flex-1 rounded-r-none bg-white/10 border-white/20 text-white placeholder-gray-400"
                  />
                  <span className="px-4 py-2 bg-white/5 border border-l-0 border-white/20 rounded-r-lg text-gray-400 flex items-center">
                    .myshopify.com
                  </span>
                </div>
                
                <Button
                  onClick={handleConnectShopify}
                  disabled={!shopDomain || connecting}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                  size="lg"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Store className="w-5 h-5 mr-2" />
                      Connect Shopify & Continue
                    </>
                  )}
                </Button>

                <p className="text-center text-gray-500 text-sm mt-4">
                  You'll be redirected to Shopify to authorize the connection
                </p>
              </div>
            </div>
          )}

          {/* Skip / Manual Signup Step */}
          {step === 'skip' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white/20">
              <button 
                onClick={() => setStep('choice')}
                className="text-gray-400 hover:text-white mb-6 flex items-center gap-2 text-sm"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back
              </button>

              <form onSubmit={handleSkipSubmit} className="max-w-md mx-auto space-y-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Building className="w-4 h-4 inline mr-2" />
                    Company Name *
                  </Label>
                  <Input
                    type="text"
                    value={skipForm.companyName}
                    onChange={(e) => handleSkipFormChange('companyName', e.target.value)}
                    placeholder="Your Company Name"
                    className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Users className="w-4 h-4 inline mr-2" />
                    Your Full Name *
                  </Label>
                  <Input
                    type="text"
                    value={skipForm.fullName}
                    onChange={(e) => handleSkipFormChange('fullName', e.target.value)}
                    placeholder="John Doe"
                    className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address *
                  </Label>
                  <Input
                    type="email"
                    value={skipForm.email}
                    onChange={(e) => handleSkipFormChange('email', e.target.value)}
                    placeholder="you@company.com"
                    className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">
                    Password *
                  </Label>
                  <Input
                    type="password"
                    value={skipForm.password}
                    onChange={(e) => handleSkipFormChange('password', e.target.value)}
                    placeholder="Min 6 characters"
                    className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">
                    Confirm Password *
                  </Label>
                  <Input
                    type="password"
                    value={skipForm.confirmPassword}
                    onChange={(e) => handleSkipFormChange('confirmPassword', e.target.value)}
                    placeholder="Confirm your password"
                    className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  size="lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
                  <p className="text-amber-300 text-sm flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    You can connect your Shopify store anytime from the Integrations page to unlock full features.
                  </p>
                </div>
              </form>
            </div>
          )}

          {/* Already have account */}
          <div className="text-center">
            <p className="text-gray-400">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-purple-400 hover:text-purple-300 underline"
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
